import React, { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { useFirmUserListQuery } from 'hooks/queries';
import { usePracticeAreasQuery } from 'hooks/queries/usePracticeAreasQuery';
import { generateFullName } from 'utils/generateFullNameUtils';
import GlobalTooltip from 'components/GlobalTooltip';
import { LoaderComponent } from 'shared/LoaderComponent';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import debounce from 'lodash.debounce';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';
import { UserDetailsContext } from 'context/userDetailsContext';
import { Button } from 'primereact/button';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';

const InvoicesFiltersV2 = ({ onFiltersChange, isFromCaseSpecificModule, isLeadSpecific }) => {
  const userContext = useContext(UserDetailsContext);
  // Filter state management
  const [searchKey, setSearchKey] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedLeadAttorneyList, setSelectedLeadAttorneyList] = useState(null);

  // Use React Query hook for practice areas with dynamic query parameters
  const { practiceAreaList } = usePracticeAreasQuery({
    loadFullList: true,
    status: 'active',
    excludeFromLoading: false,
    sort_order: 'alphabet_ascending',
    pagination_token: '',
    pagination_direction: 'forward',
  });

  const { userList, isFirmUserLoading } = useFirmUserListQuery({
    is_include_groups: false,
    active_groups: false,
    excludeFromLoading: true,
    is_confirmed_user: true,
  });

  // Map practice areas to dropdown options
  const practiceAreaOptions = useMemo(() => {
    if (practiceAreaList && practiceAreaList.length > 0) {
      return practiceAreaList.map((val) => ({
        label: val.practice_area_name,
        value: val.practice_area_id,
      }));
    }
    return [];
  }, [practiceAreaList]);

  // Helper to notify parent of filter changes
  const notifyFilterChange = useCallback(
    (updates) => {
      if (onFiltersChange) {
        onFiltersChange({
          searchKey,
          selectedDate,
          selectedPractice,
          selectedLeadAttorneyList,
          ...updates,
        });
      }
    },
    [onFiltersChange, searchKey, selectedDate, selectedPractice, selectedLeadAttorneyList]
  );

  // Create debounced search function that uses current state
  const debouncedSearchRef = useRef(null);

  useEffect(() => {
    // Create debounced function that accesses current state via closure
    debouncedSearchRef.current = debounce((value) => {
      if (onFiltersChange) {
        onFiltersChange({
          searchKey: value,
          selectedDate,
          selectedPractice,
          selectedLeadAttorneyList,
        });
      }
    }, 500); // 500ms delay

    // Cleanup on unmount
    return () => {
      if (debouncedSearchRef.current) {
        debouncedSearchRef.current.cancel();
      }
    };
  }, [onFiltersChange, selectedDate, selectedPractice, selectedLeadAttorneyList]);

  // Handlers
  const onChangeSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    // Debounce the API call - will trigger after user stops typing for 500ms
    debouncedSearchRef.current(value);
  };

  const onSearchKeyDown = (e) => {
    const code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      // Enter key pressed - cancel debounce and trigger immediately
      debouncedSearchRef.current.cancel();
      notifyFilterChange({ searchKey: e.target.value });
    }
  };

  const onChangeCalendar = (value) => {
    setSelectedDate(value);
    notifyFilterChange({ selectedDate: value });
  };

  const onChangePractice = (e) => {
    const value = [...e.value];
    setSelectedPractice(value);
    notifyFilterChange({ selectedPractice: value });
  };

  const onChangeLeadAttorney = (e) => {
    const list = e.value !== null ? [...e.value] : null;
    setSelectedLeadAttorneyList(list);
    notifyFilterChange({ selectedLeadAttorneyList: list });
  };

  const assignToItemTemplate = (option) => {
    return (
      <>
        {option?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
        {generateFullName(option)}
      </>
    );
  };

  const refreshFilters = () => {
    setSearchKey('');
    setSelectedDate('');
    setSelectedPractice(null);
    setSelectedLeadAttorneyList(null);
    notifyFilterChange({
      searchKey: '',
      selectedDate: '',
      selectedPractice: null,
      selectedLeadAttorneyList: null,
    });
  };
  const { showFilters, setShowFilters } = useFilterBreakpoint();

  return (
    <div className="">
      <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-2 p-lg-0 w-100 align-items-center justify-content-between">
        <div className="p-2 filter-wraper">
          <span className="p-input-icon-left input-shadow">
            <i className="icon-search F-size16"></i>
            <TextSnippetWrapper
              type="text"
              placeholder="Search"
              onChange={onChangeSearch}
              value={searchKey}
              onKeyDown={onSearchKeyDown}
              className="p-inputtext p-component input-search"
              autoComplete="off"
            />
          </span>
        </div>
        <div className="d-block d-lg-none w-100">
          <Button
            className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            aria-controls="invoice-filter-section"
            label="Filters"
          >
            <span
              style={{
                fontSize: 18,
                transition: 'transform 0.2s',
                display: 'inline-block',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <i className="fa fa-solid fa-angle-down"></i>
            </span>
          </Button>
        </div>
        <div
          className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
          id="invoice-filter-section"
          aria-hidden={!showFilters}
        >
          <label className="text-bold black-600 p-0 filter-label">Filter:</label>
          <div className="d-flex flex-column filter-wraper">
            <TimezoneDatePicker
              selectionMode="range"
              className="input-shadow filter-calender"
              id="icon"
              value={selectedDate}
              onChange={onChangeCalendar}
              showIcon
              placeholder="Invoice Date"
              timezone={userContext?.userDetails?.timezone}
              isEndOfDay={true}
            />
          </div>
          {!isFromCaseSpecificModule && !isLeadSpecific && (
            <div className="d-flex flex-column mx-0 filter-wraper">
              <MultiSelect
                value={selectedPractice}
                options={practiceAreaOptions}
                onChange={onChangePractice}
                placeholder="Practice Area"
                maxSelectedLabels={1}
                display="chip"
                filter
                className="input-shadow w-100 F-size14 input-width"
              />
            </div>
          )}
          <div className="d-flex flex-column mx-0 filter-wraper">
            <MultiSelect
              value={selectedLeadAttorneyList}
              options={userList}
              onChange={onChangeLeadAttorney}
              optionLabel={(v) => generateFullName(v)}
              optionValue="firm_user_id"
              placeholder="Lead Attorney"
              maxSelectedLabels={1}
              display="chip"
              filter
              className="input-shadow w-100 F-size14 input-width"
              showClear
              itemTemplate={assignToItemTemplate}
              panelFooterTemplate={isFirmUserLoading ? LoaderComponent : null}
            />
          </div>

          <div className="d-flex flex-column mx-2">
            <div className="pointer ms-0">
              <GlobalTooltip tooltip="Reset Filter">
                <i className="fas fa-sync-alt" onClick={refreshFilters}></i>
              </GlobalTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesFiltersV2;
